import { useTheme as useThemeReact } from "@bpipartners/webui";
import { createTheme } from "@bpipartners/webui";
import { colors as vbsoColors } from "./theme.colors.vbso";
import { colors as defaultColors } from "./theme.colors";
import { dimensions } from "./dimensions";
import localFont from "next/font/local";

// Font files can be colocated inside of `app`
const GSFont = localFont({
  src: "./fonts/GeneralSans-Medium.woff2",
  display: "swap",
});

export const getTheme = (appName: string) => {
  const colors = appName.toLowerCase() === "vbso" ? vbsoColors : defaultColors;

  const palette = colors.palette;
  const appColors = colors.appColors;

  // any because materialTheme typing expecting everything
  // eslint-disable-next-line

  const materialTheme = createTheme({
    spacing: 2,
    components: {
      MuiInputBase: {
        styleOverrides: {
          root: {
            backgroundColor: appColors.surfaceContainerLow,
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          variant: "outlined",
        },
        styleOverrides: {
          root: {
            borderRadius: 12,
            padding: 0,
            paddingTop: 8,
            paddingBottom: 8,
            backgroundColor: appColors.surfaceContainerLowest,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            borderRadius: 0,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            backgroundColor: appColors.surfaceContainerLowest,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          size: "small",
          variant: "outlined",
        },
        styleOverrides: {
          root: {
            borderRadius: 24,
            textTransform: "none",
          },
        },
      },
      MuiIconButton: {
        defaultProps: {
          size: "small",
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "&.Mui-disabled": {
              background: appColors.surfaceContainerLowest,
              color: "#dadada",
            },
          },
        },
      },
      MuiPickersTextField: {
        styleOverrides: {
          root: {
            backgroundColor: appColors.surfaceContainerLow,
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: palette.primary[95],
            },
            "& .MuiDataGrid-pinnedColumnHeaders": {
              backgroundColor: palette.primary[95],
            },
            "& .MuiDataGrid-pinnedColumns": {
              backgroundColor: appColors.surfaceContainerHigh,
            },
            "& .MuiDataGrid-headerFilterRow": {
              backgroundColor: palette.primary[95],
            },
            "& .MuiDataGrid-footerContainer": {
              minHeight: 40,
            },
            "& .MuiDataGrid-row.Mui-selected ": {
              backgroundColor: palette.secondary[95],
              cursor: "pointer",
            },
            "& .MuiDataGrid-row.Mui-selected:hover ": {
              backgroundColor: palette.secondary[90],
              cursor: "pointer",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: palette.secondary[90],
              cursor: "pointer",
            },
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-cell": {
              alignContent: "center",
            },
          },
        },
      },
    },
    palette,
    typography: {
      fontFamily: GSFont.style.fontFamily,
      caption: {
        fontSize: "0.85rem",
      },
    },
  });
  return {
    ...materialTheme,
    // Override material default: show header above drawer (sidebar)
    zIndex: {
      ...materialTheme.zIndex,
      appBar: materialTheme.zIndex.drawer,
      drawer: materialTheme.zIndex.appBar,
    },
    appColors: colors.appColors,
    appPalette: colors.appPalette,
    dimensions,
  };
};

export type Theme = ReturnType<typeof getTheme>;

export const useTheme = () => useThemeReact() as Theme;
